// Provides the editable business contact info (email, phone, address)
// to the whole app. Fetched once from `/api/settings/business` on mount;
// admin updates push a refresh through the same hook so the footer/JSON-LD
// reflect the new values without a hard reload.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "./api";

const FALLBACK = {
  email: "hello@localh2o.com",
  phone: "+48 881 476 730",
  address_pl: "Gdańsk, Polska",
  address_en: "Gdańsk, Poland",
  updated_at: null,
};

const BusinessSettingsContext = createContext({
  settings: FALLBACK,
  loading: true,
  refresh: async () => {},
  applyUpdate: () => {},
});

export function BusinessSettingsProvider({ children }) {
  const [settings, setSettings] = useState(FALLBACK);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/settings/business");
      setSettings({ ...FALLBACK, ...data });
    } catch (_) {
      // Network errors fall back silently — the in-memory FALLBACK keeps
      // the public site working even if the backend is briefly unreachable.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Optimistic update used by the admin form so the UI updates instantly
  // when a save succeeds, without waiting for the next /settings round-trip.
  const applyUpdate = useCallback((next) => {
    setSettings((prev) => ({ ...prev, ...next }));
  }, []);

  const value = useMemo(
    () => ({ settings, loading, refresh, applyUpdate }),
    [settings, loading, refresh, applyUpdate],
  );

  return (
    <BusinessSettingsContext.Provider value={value}>
      {children}
    </BusinessSettingsContext.Provider>
  );
}

export function useBusinessSettings() {
  return useContext(BusinessSettingsContext);
}
